import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { contractApi, ContractPageResponse, UpdateContractContactRequest } from '@/api/contract'
import { memberApi, Member } from '@/api/b2b'
import { Pencil, X, Check, User } from 'lucide-react'
import { toast } from 'sonner'

interface Props {
  data: ContractPageResponse
}

export default function ContractContactSection({ data }: Props) {
  const { t } = useTranslation('contract')
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  const { data: membersData } = useQuery({
    queryKey: ['members'],
    queryFn: () => memberApi.getAll(),
    enabled: isEditing,
  })

  const adminMembers = membersData?.data?.data?.filter((m: Member) => m.role === 'admin') ?? []

  const updateMutation = useMutation({
    mutationFn: (req: UpdateContractContactRequest) => contractApi.updateContractContact(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract'] })
      setIsEditing(false)
      setSelectedUserId(null)
      toast.success(t('contact.updateSuccess'))
    },
    onError: () => {
      toast.error(t('contact.updateError'))
    },
  })

  const startEdit = () => {
    setSelectedUserId(data.contractContact?.userId ?? null)
    setIsEditing(true)
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setSelectedUserId(null)
  }

  const handleConfirm = () => {
    if (selectedUserId === null) return
    updateMutation.mutate({ userId: selectedUserId })
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* 계약 담당자 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('contact.title')}</h3>
          {isEditing ? (
            <div className="flex gap-2">
              <button
                onClick={cancelEdit}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
                {t('contact.cancel')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={updateMutation.isPending || selectedUserId === null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                {t('contact.confirm')}
              </button>
            </div>
          ) : (
            <button
              onClick={startEdit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Pencil className="w-4 h-4" />
              {t('contact.change')}
            </button>
          )}
        </div>

        {isEditing ? (
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            value={selectedUserId ?? ''}
            onChange={(e) => setSelectedUserId(Number(e.target.value))}
          >
            <option value="" disabled>
              {t('contact.selectPlaceholder')}
            </option>
            {adminMembers.map((m: Member) => (
              <option key={m.userId} value={m.userId}>
                {m.fullName} ({m.email})
              </option>
            ))}
          </select>
        ) : data.contractContact ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-900">{data.contractContact.name}</span>
            </div>
            {data.contractContact.email && (
              <p className="text-sm text-gray-600 ml-6">{data.contractContact.email}</p>
            )}
            {data.contractContact.phone && (
              <p className="text-sm text-gray-600 ml-6">{data.contractContact.phone}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">{t('contact.noContact')}</p>
        )}
      </div>

      {/* 이쿠얼키 담당자 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('contact.equalkeyTitle')}</h3>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-900">{t('contact.equalkeyName')}</span>
          </div>
          <p className="text-sm text-gray-600 ml-6">{t('contact.equalkeyPhone')}</p>
          <p className="text-sm text-gray-600 ml-6">{t('contact.equalkeyEmail')}</p>
        </div>
      </div>
    </div>
  )
}
